import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Loader2, ThumbsUp, ThumbsDown, ArrowRight, CheckCircle2 } from "lucide-react";
import { ProspectCard } from "./StepPreview";

export const StepLaunch = ({ userId, huntJobId, onFinish }) => {
  const [job, setJob] = useState({ status: "running", stage: "Starting your first hunt", progress: 1 });
  const [verdicts, setVerdicts] = useState({});
  const timer = useRef(null);

  useEffect(() => {
    if (!huntJobId) { setJob({ status: "skipped" }); return; }
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/agent/hunt/job/${huntJobId}`);
        setJob(data);
        if (data.status === "running") timer.current = setTimeout(tick, 2500);
      } catch (e) {
        setJob({ status: "failed", error: "Could not reach the hunt" });
      }
    };
    tick();
    return () => clearTimeout(timer.current);
  }, [huntJobId]);

  const prospects = (job.result?.companiesFound || []).slice(0, 5);
  const sessionId = job.result?.huntSessionId;

  const vote = async (idx, outcome) => {
    setVerdicts((v) => ({ ...v, [idx]: outcome }));
    if (sessionId) axios.post(`${API}/prospecting/disposition/${userId}`, { huntSessionId: sessionId, index: idx, outcome }).catch(() => {});
  };
  const reviewed = Object.keys(verdicts).length;

  return (
    <div className="space-y-5" data-testid="onboarding-step-launch">
      {job.status === "running" && (
        <div className="text-center py-10" data-testid="launch-progress">
          <Loader2 className="h-10 w-10 animate-spin text-ism-teal mx-auto mb-4" />
          <h2 className="text-xl text-white font-light">Your setup is saved. Running your first hunt…</h2>
          <p className="text-gray-400 text-sm mt-2">{job.stage}</p>
          <div className="mt-4 mx-auto max-w-sm h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-ism-teal transition-all duration-500" style={{ width: `${job.progress || 1}%` }} /></div>
          <Button variant="ghost" onClick={onFinish} className="text-gray-500 hover:text-white mt-6 text-xs" data-testid="launch-skip-btn">Skip — take me to the dashboard</Button>
        </div>
      )}

      {job.status === "done" && (
        <>
          <div>
            <h2 className="text-2xl text-white font-light">{prospects.length ? `Your first ${prospects.length} prospects` : "Setup complete"}</h2>
            <p className="text-gray-400 mt-1 text-sm">{prospects.length ? "Thumbs up the ones worth a conversation, thumbs down the misses. Every vote teaches the agent." : "The first pass found no strong matches yet — the agent keeps hunting in the background and will surface prospects on your dashboard."}</p>
          </div>
          <div className="space-y-3">
            {prospects.map((p, i) => (
              <ProspectCard key={p.url || p.companyName || i} p={p} idx={i}>
                <div className="flex gap-1 ml-auto">
                  <button onClick={() => vote(i, "saved")} className={`p-1.5 rounded-lg border transition-colors ${verdicts[i] === "saved" ? "bg-ism-teal/20 border-ism-teal/40 text-ism-teal" : "border-white/10 text-gray-500 hover:text-ism-teal"}`} data-testid={`launch-approve-${i}`}><ThumbsUp className="h-4 w-4" /></button>
                  <button onClick={() => vote(i, "dismissed")} className={`p-1.5 rounded-lg border transition-colors ${verdicts[i] === "dismissed" ? "bg-red-500/20 border-red-500/40 text-red-400" : "border-white/10 text-gray-500 hover:text-red-400"}`} data-testid={`launch-reject-${i}`}><ThumbsDown className="h-4 w-4" /></button>
                </div>
              </ProspectCard>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-gray-500">{prospects.length ? `${reviewed}/${prospects.length} reviewed` : ""}</p>
            <Button onClick={onFinish} className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium rounded-xl px-6" data-testid="launch-finish-btn"><CheckCircle2 className="h-4 w-4 mr-2" /> Go to my dashboard <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </div>
        </>
      )}

      {(job.status === "failed" || job.status === "skipped") && (
        <div className="text-center py-10" data-testid="launch-fallback">
          <CheckCircle2 className="h-10 w-10 text-ism-teal mx-auto mb-4" />
          <h2 className="text-xl text-white font-light">Your sales machine is set up</h2>
          <p className="text-gray-400 text-sm mt-2">{job.error || "Run Hunt from the dashboard whenever you're ready."}</p>
          <Button onClick={onFinish} className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium rounded-xl px-6 mt-6" data-testid="launch-finish-btn">Go to my dashboard <ArrowRight className="h-4 w-4 ml-2" /></Button>
        </div>
      )}
    </div>
  );
};
